import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { SYMPTOM_CATALOG, getStatusLevel } from '../../utils/wellness';
import './WellnessLogsTab.css';

function SymptomScore({ symptom, value }) {
  if (value == null) return null;
  const level = getStatusLevel(symptom.inverted ? 10 - value : value);
  return (
    <div className="wl-symptom">
      <span className="wl-symptom-icon">{symptom.icon}</span>
      <span className="wl-symptom-label">{symptom.label}</span>
      <span className={`wl-symptom-value tag-${level}`}>
        {value}/10
      </span>
    </div>
  );
}

// Get date string in YYYY-MM-DD format
function toDateStr(date) {
  return date.toISOString().split('T')[0];
}

// Get array of last N days as YYYY-MM-DD strings
function getLast90Days() {
  const days = [];
  const now = new Date();
  for (let i = 0; i < 90; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    days.push(toDateStr(d));
  }
  return days;
}

export default function WellnessLogsTab({ clientId, client }) {
  const [logsByDate, setLogsByDate] = useState({});
  const [todoItems, setTodoItems] = useState([]);
  const [checkoffsByDate, setCheckoffsByDate] = useState({});
  const [journalsByDate, setJournalsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [dayIndex, setDayIndex] = useState(0); // 0 = today, 1 = yesterday, etc.

  const allDays = getLast90Days();
  const currentDate = allDays[dayIndex];

  // Show all symptoms that have data, plus any currently enabled
  const enabledSymptoms = client?.enabled_symptoms || [];

  useEffect(() => {
    loadData();
  }, [clientId]);

  async function loadData() {
    setLoading(true);

    const [logsRes, todosRes, checkoffsRes, journalsRes] = await Promise.all([
      supabase
        .from('daily_logs')
        .select('*, custom_answers(*, custom_questions(question_text))')
        .eq('client_id', clientId)
        .order('date', { ascending: false })
        .limit(90),
      supabase
        .from('todo_items')
        .select('*')
        .eq('client_id', clientId)
        .eq('is_active', true)
        .order('sort_order'),
      supabase
        .from('todo_checkoffs')
        .select('*, todo_items(name)')
        .eq('client_id', clientId)
        .order('date', { ascending: false })
        .limit(500),
      supabase
        .from('daily_journals')
        .select('*')
        .eq('client_id', clientId)
        .order('date', { ascending: false })
        .limit(90),
    ]);

    console.log('DailyHistory loaded:', {
      logs: logsRes.data?.length,
      logsError: logsRes.error,
      todos: todosRes.data?.length,
      checkoffs: checkoffsRes.data?.length,
      journals: journalsRes.data?.length,
      enabledSymptoms: client?.enabled_symptoms,
    });

    // Index logs by date
    const lByDate = {};
    (logsRes.data || []).forEach((l) => { lByDate[l.date] = l; });
    setLogsByDate(lByDate);

    setTodoItems(todosRes.data || []);

    // Group checkoffs by date
    const cByDate = {};
    (checkoffsRes.data || []).forEach((c) => {
      if (!cByDate[c.date]) cByDate[c.date] = [];
      cByDate[c.date].push(c);
    });
    setCheckoffsByDate(cByDate);

    // Group journals by date
    const jByDate = {};
    (journalsRes.data || []).forEach((j) => { jByDate[j.date] = j; });
    setJournalsByDate(jByDate);

    setLoading(false);
  }

  function goBack() {
    if (dayIndex < 89) setDayIndex(dayIndex + 1);
  }

  function goForward() {
    if (dayIndex > 0) setDayIndex(dayIndex - 1);
  }

  if (loading) {
    return <div className="tab-loading">Loading daily history...</div>;
  }

  const dateObj = new Date(currentDate + 'T12:00:00');
  const dateStr = dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const log = logsByDate[currentDate];
  const dayCheckoffs = checkoffsByDate[currentDate] || [];
  const dayJournal = journalsByDate[currentDate];
  const totalTodos = todoItems.length;
  const completedTodos = dayCheckoffs.length;
  const todoPct = totalTodos > 0 ? Math.round((completedTodos / totalTodos) * 100) : 0;
  // Show any symptom that has data in this log (not limited to currently enabled)
  const loggedSymptoms = log ? SYMPTOM_CATALOG.filter((s) => log[s.key] != null) : [];

  const hasAnyData = log || dayCheckoffs.length > 0 || dayJournal;

  return (
    <div className="wellness-logs-tab">
      {/* Date navigation */}
      <div className="wl-nav">
        <button
          className="wl-nav-arrow"
          onClick={goBack}
          disabled={dayIndex >= 89}
          aria-label="Previous day"
        >
          ‹
        </button>
        <div className="wl-nav-date">
          <span className="wl-nav-date-main">{dateStr}</span>
          {dayIndex === 0 && <span className="wl-nav-today-badge">Today</span>}
        </div>
        <button
          className="wl-nav-arrow"
          onClick={goForward}
          disabled={dayIndex <= 0}
          aria-label="Next day"
        >
          ›
        </button>
      </div>

      {/* Day content */}
      {!hasAnyData ? (
        <div className="wl-empty-day">
          <p>No logs for this day.</p>
        </div>
      ) : (
        <div className="wl-day-content">
          {/* To-Do completion */}
          {totalTodos > 0 && (
            <div className="wl-entry">
              <div className="wl-entry-title">To-Do Completion</div>
              <div className="wl-todo-header">
                <div className="wl-todo-bar-wrap">
                  <div className="wl-todo-bar">
                    <div className="wl-todo-bar-fill" style={{ width: `${todoPct}%` }} />
                  </div>
                </div>
                <span className={`wl-todo-pct ${todoPct === 100 ? 'wl-todo-complete' : todoPct > 0 ? 'wl-todo-partial' : 'wl-todo-none'}`}>
                  {completedTodos}/{totalTodos}
                </span>
              </div>
              {todoItems.length > 0 && (
                <div className="wl-todo-list">
                  {todoItems.map((item) => {
                    const done = dayCheckoffs.some((c) => c.todo_item_id === item.id);
                    return (
                      <div key={item.id} className={`wl-todo-item ${done ? 'wl-todo-item-done' : 'wl-todo-item-missed'}`}>
                        <span className="wl-todo-check">{done ? '✓' : '—'}</span>
                        <span>{item.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Symptom scores */}
          {loggedSymptoms.length > 0 && (
            <div className="wl-entry">
              <div className="wl-entry-title">Symptom Scores</div>
              <div className="wl-symptoms-grid">
                {loggedSymptoms.map((symptom) => (
                  <SymptomScore
                    key={symptom.key}
                    symptom={symptom}
                    value={log[symptom.key]}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Custom answers */}
          {log && log.custom_answers && log.custom_answers.length > 0 && (
            <div className="wl-entry">
              <div className="wl-entry-title">Practitioner Questions</div>
              <div className="wl-custom-answers">
                {log.custom_answers.map((ans) => (
                  <div key={ans.id} className="wl-custom-answer">
                    <span className="wl-qa-q">{ans.custom_questions?.question_text || 'Question'}</span>
                    <span className="wl-qa-a">{ans.answer_value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Journals */}
          {(log?.journal_text || dayJournal?.journal_1 || dayJournal?.journal_2) && (
            <div className="wl-entry">
              <div className="wl-entry-title">Journal</div>
              {log?.journal_text && (
                <div className="wl-journal">
                  <span className="wl-journal-label">Symptom Notes</span>
                  <p>{log.journal_text}</p>
                </div>
              )}
              {dayJournal?.journal_1 && (
                <div className="wl-journal">
                  <span className="wl-journal-label">Daily Reflection</span>
                  <p>{dayJournal.journal_1}</p>
                </div>
              )}
              {dayJournal?.journal_2 && (
                <div className="wl-journal">
                  <p>{dayJournal.journal_2}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
